import { Analytics } from '@gik/analytics';
import { AnalyticsEvents } from '@gik/analytics/utils/Events';
import Link from '@gik/core/components/Link.tsx';
import type { BreakpointType } from '@gik/core/hooks/CustomHooks';
import { useIsMounted } from '@gik/core/hooks/hooks/useIsMounted';
import type { Group, GroupSettings } from '@gik/core/models/gik/Group';
import routes from '@gik/core/routes';
import { useCan } from '@gik/core/store/permissions';
import type { UIComponent } from '@gik/core/types/UI';
import { cn } from '@gik/core/utils/classnames';
import withComponentErrorBoundary from '@gik/core/utils/withComponentErrorBoundary';
import { logoImageUrl as logoPlaceholderUrl } from '@gik/inkind-page/constants';
import { BackgroundImage } from '@gik/ui-legacy/BackgroundImage';
import { Button } from '@gik/ui-legacy/Button';
import { FlexGrid } from '@gik/ui-legacy/FlexGrid';
import { KeepProportions } from '@gik/ui-legacy/KeepProportions';
import { SvgIcon } from '@gik/ui-legacy/SvgIcon';
import { PageSection } from '@gik/ui-legacy/gik/PageSection';
import CameraIcon from '@heroicons/react/outline/CameraIcon';
import CogIcon from '@heroicons/react/solid/CogIcon';
import type React from 'react';
import classMap from '../styles/classMap';

export type InkindGroupHeaderProps = {
  group: Group;
  logoImageUrl?: string;
  muted?: boolean;
} & UIComponent &
  BreakpointType;

export const groupHeaderAspectRatio = 0.21951219512195;

function InkindGroupHeaderComp({ group, logoImageUrl, muted, className }: InkindGroupHeaderProps) {
  const isMounted = useIsMounted();

  // const canCreatePage = useCan("create", "inkinds", { groupId: group?.id }) && !!group && !muted;
  const canManageGroup = useCan('manage', 'groups', { groupId: group?.id }) && !!group && !muted;

  const settings: Partial<GroupSettings> = group?.inkindGroupSettings;

  if (!settings) return null;

  const hasGradient = settings?.styleVariant === 'gradient';
  const resolvedLogoImageUrl = logoImageUrl || group?.inkindGroupSettings?.logoImageUrl;
  const hasCustomLogo = !!resolvedLogoImageUrl && resolvedLogoImageUrl !== logoPlaceholderUrl;

  // A custom background/banner image occupies the same visual space as the
  // logo, so the "Add a Logo" placeholder must not compete with it.
  const hasBackgroundImage = settings?.styleVariant === 'image' && !!settings?.bannerImageUrl;

  return (
    <KeepProportions proportion={groupHeaderAspectRatio} className={classMap.groupHeaderKeepProp()}>
      <div className={classMap.groupHeaderWrapper()}>
        <FlexGrid className={cn(classMap.groupHeaderActions({ muted }), 'left-2')}>
          {canManageGroup && isMounted() && (
            <Button
              type={'link'}
              href={`${routes.groups}/${group.wordPressGroupSlug}/admin/settings?section=branding`}
              variant="secondary"
              circle
              size="sm"
              prepend={<SvgIcon Icon={CameraIcon} />}
              onClick={() => {
                Analytics.fireEvent(AnalyticsEvents.GroupSettingsLogoClicked, {
                  groupSlug: group.wordPressGroupSlug,
                });
                Analytics.fireEvent(AnalyticsEvents.GroupPageEditClicked, {
                  elementType: 'logo',
                  groupSlug: group.wordPressGroupSlug,
                });
              }}
            />
          )}
        </FlexGrid>
        <FlexGrid className={cn(classMap.groupHeaderActions({ muted }), 'right-2')}>
          {canManageGroup && isMounted() && (
            <Button
              type={'link'}
              href={`${routes.groups}/${group.wordPressGroupSlug}/admin/settings`}
              variant="secondary-outline"
              pill
              size="sm"
              prepend={<SvgIcon Icon={CogIcon} />}
              onClick={() => {
                Analytics.fireEvent(AnalyticsEvents.GroupSettingsIconClicked, {
                  groupSlug: group.wordPressGroupSlug,
                });
                Analytics.fireEvent(AnalyticsEvents.GroupPageEditClicked, {
                  elementType: 'settings',
                  groupSlug: group.wordPressGroupSlug,
                });
              }}
            >
              Group Settings
            </Button>
          )}
        </FlexGrid>
        <PageSection
          className={cn(classMap.groupHeaderSection({ hasGradient }), classMap.groupHeaderRoot(), className)}
          contained={false}
          noPad
          background={settings?.styleVariant === 'image' ? settings?.bannerImageUrl : undefined}
          backgroundSize="cover"
          backgroundColor={
            settings?.styleVariant === 'solid' || !settings?.styleVariant ? settings?.brandColor : undefined
          }
          style={
            {
              '--gradient-color-1': settings?.gradientColor1,
              '--gradient-color-2': settings?.gradientColor2,
              background:
                hasGradient && settings?.gradientColor1 && settings?.gradientColor2
                  ? `linear-gradient(135deg, ${settings.gradientColor1} -0.2%, ${settings.gradientColor2} 99.8%)`
                  : undefined,
            } as React.CSSProperties
          }
        >
          {/* A genuine custom logo always renders, in every context and over
              any background. The "Your Company" placeholder is a preview-only
              affordance (muted = admin preview) shown so admins see where a
              logo lands — but it must NOT compete with a background image, so
              it is suppressed the moment an image background is selected. The
              real page never shows the placeholder at all. */}
          {(hasCustomLogo || (muted && !hasBackgroundImage)) && (
            <BackgroundImage
              src={hasCustomLogo ? resolvedLogoImageUrl : logoPlaceholderUrl}
              className={classMap.groupHeaderLogo()}
              style={{
                width: '80%',
                height: '60%',
                marginTop: '1rem',
                backgroundSize: 'contain',
                backgroundPosition: '50% 50%',
                backgroundRepeat: 'no-repeat',
              }}
            />
          )}
          {!hasCustomLogo && !hasBackgroundImage && canManageGroup && isMounted() && (
            <Link
              href={`${routes.groups}/${group.wordPressGroupSlug}/admin/settings?section=branding`}
              className={classMap.groupHeaderLogoFallback()}
              onClick={() => {
                Analytics.fireEvent(AnalyticsEvents.GroupPageEditClicked, {
                  elementType: 'logo',
                  groupSlug: group.wordPressGroupSlug,
                });
              }}
            >
              Add a Logo
            </Link>
          )}
        </PageSection>
      </div>
    </KeepProportions>
  );
}

export const InkindGroupHeader = withComponentErrorBoundary(InkindGroupHeaderComp);
